// Pure helpers for the Inventory assistant screens (spec: inventory-assistant).
//
// Everything that decides a word, a colour or a request lives here so it can be proved with the
// bare jest that ships with react-scripts. No JSX, no antd and no `src/config` import belongs in this
// file.

// --- The change log --------------------------------------------------------------------------

const ACTION_LABELS = {
  create: 'Created',
  update: 'Edited',
  status: 'Status changed',
  status_override: 'Went live (checks waived)',
  msrp: 'MSRP set',
  undo: 'Undone',
  job: 'Changed by a check'
}

export const actionLabel = action => ACTION_LABELS[action] || action || '—'

// Who did it. The bot's own edits say Telegram; the background checks say which check.
export const viaLabel = via => {
  const v = String(via || '')
  if (v === 'telegram') return 'Telegram bot'
  if (v.indexOf('job:') === 0) return `Background check (${v.slice(4)})`
  return v || '—'
}

// A colour for the action, so a row of undone/waived changes stands out from routine ones.
export const actionColor = action => {
  if (action === 'status_override') return 'orange'
  if (action === 'undo') return 'default'
  if (action === 'job') return 'purple'
  if (action === 'create') return 'green'
  return 'blue'
}

// "Profit: 1,000 → 3,000" for one entry of `fieldsChanged`.
export const changeLine = change => `${change.label}: ${change.before} → ${change.after}`

// The query string for the log. Empty values are left out; the date range goes as ?from= and ?to=.
export const buildChangesQuery = ({ car, actor, range, via, page } = {}) => {
  const params = []
  const add = (k, v) => {
    if (v !== undefined && v !== null && v !== '') params.push(`${k}=${encodeURIComponent(v)}`)
  }
  add('car', car && String(car).trim())
  add('actor', actor)
  add('via', via && String(via).trim())
  if (range && range[0]) add('from', range[0])
  if (range && range[1]) add('to', range[1])
  add('page', page && page > 1 ? page : '')
  return params.length ? `?${params.join('&')}` : ''
}

// --- Cost -------------------------------------------------------------------------------------

// Spend is tiny per call, so show enough decimals to be meaningful and never "$0.00" for a real cost.
export const formatUsd = n => {
  const value = Number(n || 0)
  if (value === 0) return '$0'
  if (value < 0.01) return `$${value.toFixed(4)}`
  return `$${value.toFixed(2)}`
}

export const formatTokens = n => Number(n || 0).toLocaleString()

// chart.js wants parallel arrays.
export const spendSeries = daily => ({
  labels: (daily || []).map(d => String(d.day)),
  data: (daily || []).map(d => Number(d.cost || 0))
})

// --- Health -----------------------------------------------------------------------------------

export const canaryDisplay = canary => {
  if (!canary) return { color: 'default', label: 'Not run yet' }
  if (canary.ok) return { color: 'green', label: 'Scrapers reading their test pages' }
  return {
    color: 'red',
    label: 'A scraper has stopped reading its test page',
    problems: canary.problems || []
  }
}

// The one tag that says how much of the month's Zyte allowance is used (spec: phone-first-scraping
// Req 4.1-4.3). null when the API does not send the block yet, so an undeployed API renders nothing
// rather than "Fetches: 0 of 0".
export const fetchDisplay = fetching => {
  if (!fetching) return null
  const fetchers = fetching.fetchers || []
  if (!fetchers.includes('zyte')) return { label: 'Zyte off', color: undefined }
  if (!fetching.zyteConfigured) return { label: 'Zyte not configured', color: 'red' }
  const used = fetching.zyteThisMonth || 0
  const cap = fetching.zyteCap || 0
  const ratio = cap ? used / cap : 0
  const color = ratio >= 1 ? 'red' : ratio >= 0.8 ? 'orange' : undefined
  return {
    label: `Fetches: ${used.toLocaleString()} of ${cap.toLocaleString()} (Zyte)`,
    color
  }
}

// The rungs the API will try, in order, and whether each can actually run there (spec:
// scrape-strategy-ladder Req 5.3). A rung that is configured but unusable — its library missing
// from the image, its key unset — is otherwise invisible until a fetch quietly skips it.
// null when the API does not send the block yet.
export const ladderDisplay = fetching => {
  if (!fetching || !Array.isArray(fetching.ladder) || !fetching.ladder.length) return null
  return fetching.ladder.map(rung => ({
    name: rung.name,
    label: RUNG_LABELS[rung.name] || rung.name,
    // Red is for a rung that was asked for and cannot run; paid rungs are worth seeing at a glance.
    color: rung.available ? (rung.paid ? 'blue' : undefined) : 'red',
    title: rung.available
      ? rung.paid
        ? 'Costs money — tried last'
        : 'Free'
      : 'Configured but not usable here'
  }))
}

export const RUNG_LABELS = {
  http: 'Plain',
  curl: 'TLS',
  browser: 'Browser',
  native: 'Browser',
  stealth: 'Stealth',
  firefox: 'Firefox',
  zyte: 'Zyte'
}

export const botDisplay = bot => {
  if (!bot.tokenConfigured) return { color: 'red', label: 'No token on the server' }
  if (!bot.webhookRegistered) return { color: 'orange', label: 'Webhook not registered' }
  if (bot.lastError) return { color: 'orange', label: 'Telegram reported an error' }
  return { color: 'green', label: 'Connected' }
}

// --- Cars list --------------------------------------------------------------------------------

// The tag on a car in the list: ready, or how many things it still needs (with the list for a tooltip).
export const readinessTag = readiness => {
  if (!readiness) return null
  if (readiness.ready) return { color: 'green', label: 'Ready', items: [] }
  const items = (readiness.missing || []).map(m => m.label)
  return {
    color: 'volcano',
    label: `Needs ${items.length} thing${items.length === 1 ? '' : 's'}`,
    items
  }
}

// --- People -----------------------------------------------------------------------------------

// What to send to add or change a person. The staff member is named by phone number or email: there
// is no user picker, and a person knows a phone number, not an id.
export const personPayload = values => {
  const payload = {
    telegramUserId: Number(values.telegramUserId),
    displayName: (values.displayName || '').trim(),
    bots: values.bots || [],
    isActive: values.isActive !== false
  }
  const who = (values.userIdentifier || '').trim()
  if (who) payload.userIdentifier = who
  return payload
}

// The server's own sentence for a refusal, falling back to ours.
export const errorSentence = (response, fallback) => {
  const data = response && response.error && response.error.response && response.error.response.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const first = Object.keys(data)[0]
  if (first) {
    const value = data[first]
    return Array.isArray(value) ? value[0] : String(value)
  }
  return fallback
}

export const formatDateTime = iso => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.toDateString()} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}
